import React from 'react';
import { useIntl } from 'react-intl';

import Typography from 'carbon-react/lib/components/typography';

import { Sidebar } from '@futrli/ui-components';

import {
  AnalysisFeedbackDraft,
  AnalysisFeedbackRating,
  AnalysisFeedbackRecord,
  AnalysisFeedbackSaveResult
} from './analysis-feedback-types';
import { AnalysisFeedbackFormScreen } from './screens/analysis-feedback-form-screen';
import { AnalysisFeedbackLoadErrorScreen } from './screens/analysis-feedback-load-error-screen';
import { AnalysisFeedbackLoadingScreen } from './screens/analysis-feedback-loading-screen';
import { AnalysisFeedbackSuccessScreen } from './screens/analysis-feedback-success-screen';
import { useAnalysisFeedbackForm } from './hooks/use-analysis-feedback-form';

interface AnalysisFeedbackSidebarProps {
  feedback: AnalysisFeedbackRecord | null;
  initialRating: AnalysisFeedbackRating | null;
  isLoading?: boolean;
  isOpen: boolean;
  loadError?: boolean;
  onClose: () => void;
  onRetryLoad?: () => Promise<unknown> | void;
  onSave: (draft: AnalysisFeedbackDraft) => Promise<AnalysisFeedbackSaveResult | void> | void;
}

export const AnalysisFeedbackSidebar = ({
  feedback,
  initialRating,
  isLoading = false,
  isOpen,
  loadError = false,
  onClose,
  onRetryLoad = () => undefined,
  onSave
}: AnalysisFeedbackSidebarProps) => {
  const intl = useIntl();
  const form = useAnalysisFeedbackForm({ feedback, initialRating, isOpen, onClose, onSave });

  let screen = <AnalysisFeedbackFormScreen feedback={feedback} form={form} onCancel={form.handleCancel} />;

  if (isLoading || form.isSubmitting) {
    screen = <AnalysisFeedbackLoadingScreen isSubmitting={form.isSubmitting} />;
  } else if (loadError) {
    screen = <AnalysisFeedbackLoadErrorScreen onCancel={form.handleCancel} onRetryLoad={onRetryLoad} />;
  } else if (form.showSuccess) {
    screen = <AnalysisFeedbackSuccessScreen onCancel={form.handleCancel} />;
  }

  return (
    <Sidebar
      disableEscKey={form.isSubmitting}
      focusFirstElement={form.focusFirstElement}
      header={
        <Typography fontWeight="700" variant="h2">
          {intl.formatMessage({
            id: 'aiInsights.feedback.title',
            defaultMessage: 'Help improve your analysis'
          })}
        </Typography>
      }
      onCancel={form.isSubmitting ? undefined : form.handleCancel}
      open={isOpen}
      restoreFocusOnClose
    >
      {screen}
    </Sidebar>
  );
};
